import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { VehicleBlFormService } from 'src/app/services/bl-form/vehicle-bl-form.service';
import { GeneralService } from 'src/app/services/general/general.service';

declare var bootstrap: any;

export interface ViewBlFormData {
  blForm: any;
  amendmentNumber: number;
}

@Component({
  selector: 'app-view-bl-form',
  templateUrl: './view-bl-form.component.html',
  styleUrls: ['./view-bl-form.component.scss']
})
export class ViewBlFormComponent implements OnInit, OnDestroy {

  @Output() blNoUpdated = new EventEmitter<any>();

  blForm: any = null;
  order: any = null;
  amendment: any = null;
  amendmentNumber: number = 1;
  setting: any = null;

  // BL No editing
  blNo: string = '';
  originalBlNo: string = '';
  blNoChanged: boolean = false;
  isSavingBlNo: boolean = false;
  excelImporting: boolean = false;
  // PDF generation
  isGeneratingPdf: boolean = false;
  pdfUrl: string = '';

  private subscriptions: Subscription[] = [];

  constructor(
    private _globalStateService: GlobalStateService,
    private _vehicleBlFormService: VehicleBlFormService,
    private _generalService: GeneralService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this._globalStateService.GetSetting().subscribe((res: any) => {
        if (res) {
          this.setting = res;
        }
      })
    );
    this.setupModalListener();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_view_bl_form');
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetData();
      });
    }
  }

  openModal(blForm: any, amendmentNumber: number): void {
    this.blForm = blForm;
    this.order = blForm.order;
    this.amendmentNumber = amendmentNumber;
    this.amendment = blForm[`bl_amendment_${amendmentNumber}`];

    // Initialize BL No
    this.blNo = this.amendment.bl_no || '';
    this.originalBlNo = this.blNo;
    this.blNoChanged = false;

    const modalElement = document.getElementById('kt_modal_view_bl_form');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  private resetData(): void {
    this.blForm = null;
    this.order = null;
    this.amendment = null;
    this.amendmentNumber = 1;
    this.blNo = '';
    this.originalBlNo = '';
    this.blNoChanged = false;
    this.pdfUrl = '';
  }

  onBlNoChange(value: string): void {
    this.blNo = value;
    this.blNoChanged = value !== this.originalBlNo;
  }

  saveBlNo(): void {
    if (!this.blForm || !this.blNoChanged) return;

    this.isSavingBlNo = true;
    const payload = {
      bl_no: this.blNo,
      bl_amendment: this.amendmentNumber
    };

    this._vehicleBlFormService.UpdateValueData(this.blForm._id, payload).subscribe({
      next: (response: any) => {
        if (response.status) {
          this.originalBlNo = this.blNo;
          this.blNoChanged = false;
          this.blForm.bl_no = this.blNo;
          this.blNoUpdated.emit(this.blForm);
        }
        this.isSavingBlNo = false;
      },
      error: (error) => {
        console.error('Error saving BL No:', error);
        this.isSavingBlNo = false;
      }
    });
  }

  generatePdf(): void {
    if (!this.blForm) return;

    this.isGeneratingPdf = true;

    const data = {
      bl_form_id: this.blForm._id,
      bl_amendment_number: this.amendmentNumber,
      type: 'vehicle-bl-form'
    };

    this.subscriptions.push(
      this._generalService.generatePDF(data).subscribe({
        next: (res: any) => {
          this.isGeneratingPdf = false;
          if (res && res.data && res.data.url) {
            // this.pdfUrl = res.data.url;
            window.open(res.data.url, '_blank');
          }
        },
        error: (error: any) => {
          this.isGeneratingPdf = false;
          console.error('Error generating PDF:', error);
        }
      })
    );
  }

  openPdf(): void {
    if (this.pdfUrl) {
      window.open(this.pdfUrl, '_blank');
    }
  }

  exportToExcel(): void {

    this.excelImporting = true;

    this.subscriptions.push(
      this._generalService.generateTemplateExcel({
        type:"bl-amend",
        bl_form_id: this.blForm._id,
        bl_amendment_number: this.amendmentNumber
      }).subscribe({
        next: (res: any) => {
          if (res && res.data && res.data.url) {
            window.open(res.data.url, '_blank');
          }
        },
        error: (error: any) => {
          console.error('Error generating Excel:', error);
        },
        complete: () => {
          this.excelImporting = false;
        }
      })
    )

    const url = this._generalService.generateTemplateExcel({});

    // window.open(url, '_blank');


    console.log('Export to Excel clicked');
  }

  getCurrentDate(): string {
    const date = new Date();
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getDate().toString().padStart(2, '0')}, ${date.getFullYear()}`;
  }

  getStockNumber(): string {
    if (this.order?.vehicles?.length > 0) {
      return this.order.vehicles[0].car_no || '-';
    }
    return '-';
  }

  getVslVoy(): string {
    return this.amendment?.ship_name || '-';
  }

  getPortOfLoading(): string {
    return this.amendment?.sailing_port || this.order?.from_port || '-';
  }

  getPortOfDischarge(): string {
    const port = this.amendment?.destination_port || this.order?.to_port || '';
    const country = this.amendment?.destination_country || this.order?.to_country || '';
    return port && country ? `${port}, ${country}` : (port || country || '-');
  }

  getBlNo(): string {
    return this.blForm?.bl_no || this.order?.order_number || '-';
  }

  // Original Shipper Info
  getOriginalShipperInfo(): string {
    const shipper = this.order?.shipper_details || {};
    const lines = [];
    if (shipper.company_name) lines.push(shipper.company_name);
    if (shipper.address_1) lines.push(shipper.address_1);
    if (shipper.city || shipper.state) lines.push([shipper.city, shipper.state].filter(Boolean).join(', '));
    if (shipper.country) lines.push(shipper.country);
    if (shipper.zip) lines.push(shipper.zip);
    if (shipper.telephone_number) lines.push(`TEL ${shipper.telephone_number}`);
    return lines.join('\n');
  }

  // Amendment Shipper Info
  getAmendmentShipperInfo(): string {
    if (!this.amendment) return '';
    const lines = [];
    if (this.amendment.shipper_company) lines.push(this.amendment.shipper_company);
    if (this.amendment.shipper_address) lines.push(this.amendment.shipper_address);
    if (this.amendment.shipper_city) lines.push(this.amendment.shipper_city);
    if (this.amendment.shipper_country) lines.push(this.amendment.shipper_country);
    if (this.amendment.shipper_zip) lines.push(this.amendment.shipper_zip);
    if (this.amendment.shipper_tel) lines.push(`TEL ${this.amendment.shipper_tel}`);
    if (this.amendment.shipper_fax) lines.push(`FAX: ${this.amendment.shipper_fax}`);
    if (this.amendment.shipper_email) lines.push(`EMAIL: ${this.amendment.shipper_email}`);
    return lines.join('\n');
  }

  // Original Consignee Info
  getOriginalConsigneeInfo(): string {
    if (!this.order) return '';
    const lines = [];
    if (this.order.buyer_company_name) lines.push(this.order.buyer_company_name);
    if (this.order.buyer_consignee_name) lines.push(this.order.buyer_consignee_name);
    if (this.order.buyer_street_address) lines.push(this.order.buyer_street_address);
    if (this.order.buyer_city) lines.push(this.order.buyer_city);
    if (this.order.buyer_country) lines.push(this.order.buyer_country);
    if (this.order.buyer_zip) lines.push(this.order.buyer_zip);
    if (this.order.buyer_telephone_no) lines.push(`TEL ${this.order.buyer_telephone_no}`);
    if (this.order.buyer_fax) lines.push(`FAX: ${this.order.buyer_fax}`);
    if (this.order.buyer_email_address) lines.push(`EMAIL: ${this.order.buyer_email_address}`);
    return lines.join('\n');
  }

  // Amendment Consignee Info
  getAmendmentConsigneeInfo(): string {
    if (!this.amendment) return '';
    const lines = [];
    if (this.amendment.consignee_company) lines.push(this.amendment.consignee_company);
    if (this.amendment.consignee_name) lines.push(this.amendment.consignee_name);
    if (this.amendment.consignee_address) lines.push(this.amendment.consignee_address);
    if (this.amendment.consignee_city) lines.push(this.amendment.consignee_city);
    if (this.amendment.consignee_country) lines.push(this.amendment.consignee_country);
    if (this.amendment.consignee_zip) lines.push(this.amendment.consignee_zip);
    if (this.amendment.consignee_tel) lines.push(`TEL ${this.amendment.consignee_tel}`);
    if (this.amendment.consignee_fax) lines.push(`FAX: ${this.amendment.consignee_fax}`);
    if (this.amendment.consignee_email) lines.push(`EMAIL: ${this.amendment.consignee_email}`);
    return lines.join('\n');
  }

  // Original Notify Party Info
  getOriginalNotifyPartyInfo(): string {
    if (!this.order) return '';
    const lines = [];
    if (this.order.same_as_consignee) {
      if (this.order.buyer_consignee_name) lines.push(this.order.buyer_consignee_name);
      if (this.order.buyer_street_address) lines.push(this.order.buyer_street_address);
      if (this.order.buyer_city) lines.push(this.order.buyer_city);
      if (this.order.buyer_country) lines.push(this.order.buyer_country);
      if (this.order.buyer_telephone_no) lines.push(`TEL ${this.order.buyer_telephone_no}`);
      if (this.order.buyer_email_address) lines.push(`EMAIL: ${this.order.buyer_email_address}`);
    } else {
      if (this.order.notify_party_name) lines.push(this.order.notify_party_name);
      if (this.order.notify_party_address) lines.push(this.order.notify_party_address);
      if (this.order.notify_party_city) lines.push(this.order.notify_party_city);
      if (this.order.notify_party_country) lines.push(this.order.notify_party_country);
      if (this.order.notify_party_telephone) lines.push(`TEL ${this.order.notify_party_telephone}`);
      if (this.order.notify_party_email) lines.push(`EMAIL: ${this.order.notify_party_email}`);
    }
    return lines.join('\n');
  }

  // Amendment Notify Party Info
  getAmendmentNotifyPartyInfo(): string {
    if (!this.amendment) return '';
    const lines = [];
    if (this.amendment.notify_party_name) lines.push(this.amendment.notify_party_name);
    if (this.amendment.notify_party_address) lines.push(this.amendment.notify_party_address);
    if (this.amendment.notify_party_city) lines.push(this.amendment.notify_party_city);
    if (this.amendment.notify_party_country) lines.push(this.amendment.notify_party_country);
    if (this.amendment.notify_party_zip) lines.push(this.amendment.notify_party_zip);
    if (this.amendment.notify_party_tel) lines.push(`TEL ${this.amendment.notify_party_tel}`);
    if (this.amendment.notify_party_fax) lines.push(`FAX: ${this.amendment.notify_party_fax}`);
    if (this.amendment.notify_party_email) lines.push(`EMAIL: ${this.amendment.notify_party_email}`);
    return lines.join('\n');
  }

  // Get vehicles that are included in amendment
  getIncludedVehicles(): any[] {
    if (!this.order?.vehicles) return [];
    return this.order.vehicles.filter((_: any, index: number) => {
      return this.amendment?.[`vehicle_${index}_include`] === true;
    });
  }

  // Get Original Vehicle Info
  getOriginalVehicleInfo(vehicleIndex: number): string {
    const vehicle = this.order?.vehicles?.[vehicleIndex];
    if (!vehicle) return '';
    const lines = [];
    if (vehicle.maker_name) lines.push(`Make: ${vehicle.maker_name}`);
    if (vehicle.name) lines.push(`Model: ${vehicle.name}`);
    if (vehicle.fuel_type) lines.push(`Fuel: ${vehicle.fuel_type}`);
    if (vehicle.engine) lines.push(`Engine CC: ${vehicle.engine}`);
    if (vehicle.mileage) lines.push(`Mileage: ${vehicle.mileage}`);
    if (vehicle.hs_code) lines.push(`HS Code: ${vehicle.hs_code}`);
    return lines.join('\n');
  }

  // Get Amendment Vehicle Info
  getAmendmentVehicleInfo(vehicleIndex: number): string {
    if (!this.amendment) return '';
    const lines = [];
    const prefix = `vehicle_${vehicleIndex}_`;
    if (this.amendment[`${prefix}make`]) lines.push(`Make: ${this.amendment[`${prefix}make`]}`);
    if (this.amendment[`${prefix}model`]) lines.push(`Model: ${this.amendment[`${prefix}model`]}`);
    if (this.amendment[`${prefix}fuel`]) lines.push(`Fuel: ${this.amendment[`${prefix}fuel`]}`);
    if (this.amendment[`${prefix}engine_cc`]) lines.push(`Engine CC: ${this.amendment[`${prefix}engine_cc`]}`);
    if (this.amendment[`${prefix}mileage`]) lines.push(`Mileage: ${this.amendment[`${prefix}mileage`]}`);
    if (this.amendment[`${prefix}hs_code`]) lines.push(`HS Code: ${this.amendment[`${prefix}hs_code`]}`);
    if (this.amendment[`${prefix}model_code`]) lines.push(`Model Code: ${this.amendment[`${prefix}model_code`]}`);
    if (this.amendment[`${prefix}lwh`]) lines.push(`LWH: ${this.amendment[`${prefix}lwh`]}`);
    if (this.amendment[`${prefix}engine_no`]) lines.push(`Engine No: ${this.amendment[`${prefix}engine_no`]}`);
    return lines.join('\n');
  }

  // Original Shipping Details
  getOriginalShippingDetails(): string {
    if (!this.order) return '';
    const lines = [];
    if (this.order.ship_name) lines.push(`Ship Name: ${this.order.ship_name}`);
    if (this.order.from_port) lines.push(`Sailing Port: ${this.order.from_port}`);
    if (this.order.to_port) lines.push(`Destination Port: ${this.order.to_port}`);
    if (this.order.to_country) lines.push(`Destination Country: ${this.order.to_country}`);
    return lines.join('\n');
  }

  // Amendment Shipping Details
  getAmendmentShippingDetails(): string {
    if (!this.amendment) return '';
    const lines = [];
    if (this.amendment.ship_name) lines.push(`Ship Name: ${this.amendment.ship_name}`);
    if (this.amendment.carrier_name) lines.push(`Carrier Name: ${this.amendment.carrier_name}`);
    if (this.amendment.etd_date) lines.push(`ETD Date: ${this.amendment.etd_date}`);
    if (this.amendment.eta_date) lines.push(`ETA Date: ${this.amendment.eta_date}`);
    if (this.amendment.sailing_port) lines.push(`Sailing Port: ${this.amendment.sailing_port}`);
    if (this.amendment.destination_port) lines.push(`Destination Port: ${this.amendment.destination_port}`);
    if (this.amendment.destination_country) lines.push(`Destination Country: ${this.amendment.destination_country}`);
    if (this.amendment.used_vehicle) lines.push(`Used Vehicle: ${this.amendment.used_vehicle}`);
    if (this.amendment.remarks) lines.push(`Remarks: ${this.amendment.remarks}`);
    return lines.join('\n');
  }

  // Check flags
  hasShipperAmendment(): boolean {
    return this.amendment?.shipper_information === true;
  }

  hasConsigneeAmendment(): boolean {
    return this.amendment?.consignee_information === true;
  }

  hasNotifyPartyAmendment(): boolean {
    return this.amendment?.notify_party_information === true;
  }

  hasShippingDetailsAmendment(): boolean {
    return this.amendment?.shipping_details === true;
  }

  hasVehicleAmendment(vehicleIndex: number): boolean {
    return this.amendment?.[`vehicle_${vehicleIndex}_include`] === true;
  }

  hasAnyAmendment(): boolean {
    if (!this.amendment) return false;

    // Check static flags
    if (this.hasShipperAmendment() || this.hasConsigneeAmendment() ||
        this.hasNotifyPartyAmendment() || this.hasShippingDetailsAmendment()) {
      return true;
    }

    // Check vehicle flags
    const vehicleCount = this.order?.vehicles?.length || 0;
    for (let i = 0; i < vehicleCount; i++) {
      if (this.hasVehicleAmendment(i)) return true;
    }

    return false;
  }
}
